import { supabase } from "@/integrations/supabase/client";
import type { NotificationType } from "@/types/notifications";

interface CreateNotificationParams {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  metadata?: Record<string, unknown>;
}

export const createNotification = async ({
  userId,
  type,
  title,
  message,
  metadata = {},
}: CreateNotificationParams): Promise<boolean> => {
  try {
    const { error } = await (supabase as any)
      .from("notifications")
      .insert({
        user_id: userId,
        type,
        title,
        message,
        metadata,
      });

    if (error) throw error;
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error creating notification:", error);
    }
    return false;
  }
};

// Helper functions for specific notification types
export const notifyPickupApproved = (userId: string, date: string, timeSlot: string) =>
  createNotification({
    userId,
    type: "pickup_approved",
    title: "Pickup Confirmed!",
    message: `Your pickup has been scheduled for ${date} at ${timeSlot}.`,
    metadata: { date, timeSlot },
  });

export const notifyPickupDenied = (userId: string, reason: string) =>
  createNotification({
    userId,
    type: "pickup_denied",
    title: "Pickup Request Denied",
    message: reason || "Your pickup request could not be approved at this time.",
    metadata: { reason },
  });

export const notifyPickupReschedule = (userId: string, newDate: string, newTimeSlot: string) =>
  createNotification({
    userId,
    type: "pickup_reschedule_requested",
    title: "New Pickup Time Proposed",
    message: `A new pickup time has been proposed: ${newDate} at ${newTimeSlot}. Please confirm or request a different time.`,
    metadata: { newDate, newTimeSlot },
  });

export const notifyPackageDenied = (userId: string, retailer: string, reason: string) =>
  createNotification({
    userId,
    type: "package_denied",
    title: "Package Not Approved",
    message: `Your ${retailer} return was not approved: ${reason}`,
    metadata: { retailer, reason },
  });

export const notifyPickupAutoCanceled = (userId: string) =>
  createNotification({
    userId,
    type: "pickup_auto_canceled",
    title: "Pickup Canceled",
    message: "Your scheduled pickup was canceled because no packages remain.",
    metadata: {},
  });
